import { Button, Stack, useTheme } from '@mui/material'
import dayjs from 'dayjs'
import { useFormik } from 'formik'
import { FC, useEffect } from 'react'
import {
    ReplyScheduleRepeatEnum,
    ReplyScheduleWeekDaysEnum,
    useReplyScheduleUpdateApiV1ReplyScheduleReplyScheduleIdPatch,
} from '../../api/gen/base'
import { ModalDialogWrapper } from '../../components/ModalDialogWrapper'
import { Notistack } from '../../components/Notistack'
import { RegularRequestForm } from '../../components/RegularRequestForm'
import {
    RegularRequestFormFile,
    regularRequestFormValidationSchema,
    RegularRequestFormValues,
} from '../../components/RegularRequestForm/RegularRequestForm'
import { useWrapperStore } from '../../store/storeWrapper'
import { splitRegularRequestFormFiles } from '../../utils/regularRequests'

export type RegularRequestEditDialogData = {
    threadId: number
    replyScheduleId: number
    text: string
    files: RegularRequestFormFile[]
    startDateTime: string
    repeatType: ReplyScheduleRepeatEnum
    repeatDays?: ReplyScheduleWeekDaysEnum[]
}

type Props = {
    open: boolean
    initialData: RegularRequestEditDialogData | null
    onClose: VoidFunction
    onSubmit?: VoidFunction
    onDelete: (replyScheduleId: number) => void
}

export const RegularRequestEditDialog: FC<Props> = ({
    open,
    initialData,
    onClose,
    onSubmit,
    onDelete,
}) => {
    const { breakpoints } = useTheme()

    const { uploadFiles, isLoadingUploadFiles } = useWrapperStore((state) => state)

    const { isSuccess, isError, mutate, isPending, reset } =
        useReplyScheduleUpdateApiV1ReplyScheduleReplyScheduleIdPatch()

    const initialValues: RegularRequestFormValues = {
        files: initialData?.files ?? [],
        text: initialData?.text ?? '',
        requestSchedule: [
            {
                startDateTime: dayjs(initialData?.startDateTime),
                repeatType: initialData?.repeatType ?? 'ONCE',
                repeatDays: initialData?.repeatDays,
            },
        ],
    }

    const handleOpenDeleteDialog = () => {
        if (initialData) {
            onDelete(initialData.replyScheduleId)
        }
    }

    const formik = useFormik<RegularRequestFormValues>({
        initialValues,
        validationSchema: regularRequestFormValidationSchema,
        validateOnMount: true,
        onSubmit: async (formData) => {
            if (!initialData) {
                return
            }

            const { requestSchedule, text, files } = formData
            const requestData = requestSchedule[0]

            const threadId = initialData.threadId

            const { fileUrls, newFiles } = splitRegularRequestFormFiles(files)

            const filesUrl = await uploadFiles({ files: newFiles }, threadId)

            mutate({
                reply_schedule_id: initialData.replyScheduleId,
                data: {
                    send_time: requestData.startDateTime.toISOString(),
                    repeat: requestData.repeatType,
                    weekdays: requestData.repeatDays,
                    text,
                    files: [...fileUrls, ...filesUrl],
                },
            })
        },
    })

    useEffect(() => {
        if (isSuccess) {
            onSubmit?.()
            onClose?.()
        }
    }, [isSuccess])

    useEffect(() => {
        if (open) {
            formik.resetForm({ values: initialValues })
        }
    }, [open])

    const isLoading = isPending || isLoadingUploadFiles

    return (
        <>
            <ModalDialogWrapper
                open={open}
                handleClose={onClose}
                title="Редактировать регулярный запрос"
                sx={{
                    '.MuiPaper-root': {
                        width: '647px',
                        maxWidth: '647px',
                        height: 'auto',
                    },

                    '.MuiDialogActions-root': {
                        borderTop: '1px solid #F4F4F6',
                    },

                    [breakpoints.down('md')]: {
                        '.MuiPaper-root': {
                            m: 0,
                            borderRadius: 0,
                            height: '100%',
                            maxHeight: 'unset',
                        },
                    },
                }}
                footerActions={
                    <>
                        <Stack
                            sx={{
                                width: '100%',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                gap: 2,

                                [breakpoints.down('md')]: {
                                    flexDirection: 'column',
                                    gap: 1,
                                },
                            }}>
                            <Button
                                variant="outlined"
                                onClick={handleOpenDeleteDialog}
                                color="error">
                                Удалить
                            </Button>
                            <Stack
                                sx={{
                                    flexDirection: 'row',
                                    gap: 2,

                                    [breakpoints.down('md')]: {
                                        width: '100%',
                                        flexDirection: 'column-reverse',
                                        gap: 1,
                                    },
                                }}>
                                <Button variant="outlined" onClick={onClose}>
                                    Отмена
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={() => formik.handleSubmit()}
                                    disabled={!formik.dirty || !formik.isValid || isLoading}>
                                    Сохранить
                                </Button>
                            </Stack>
                        </Stack>
                    </>
                }>
                <RegularRequestForm formik={formik} disabled={isLoading} />
            </ModalDialogWrapper>
            <Notistack
                isError={isError}
                open={isError || isSuccess}
                message={isError ? 'Не удалось сохранить изменения' : 'Данные успешно сохранены'}
                onClose={() => reset()}
            />
        </>
    )
}
